import React, { useEffect, useState } from "react";
import ExportButton from "./ExportButton";
import { useListParams } from "hooks/useListParams";
import Pagination from "./common/Pagination";
import { FilterDrawer } from "./common/Drawer";
import ApiKeySelect from "./common/ApiKeySelect";
import { apiKeyLabel, useApiKeyNames } from "hooks/useApiKeyNames";
import { useParams } from "react-router-dom";
import { CommunicationService, PhoneNumber } from "services/CommunicationService";
import { CustomerService } from "../services/CustomerService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PAGE_SIZE = 10;

const emptyFilters = { apiKeyId: '', phoneNumbers: '', fullName: '', blockReason: '', status: '', createdFrom: '', createdTo: '' };

const CustomerPhoneNumbersList: React.FC = () => {
  const { customerId: companyId } = useParams<{ customerId: string }>();

  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const apiKeyNames = useApiKeyNames(phoneNumbers.map((row) => row.apiKeyId));
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { pageNumber, setPageNumber, appliedFilters, setAppliedFilters } = useListParams(emptyFilters);
  // The API Key dropdown narrows the company-wide list to one key.
  const apiKeyId = appliedFilters.apiKeyId || undefined;
  const customerId = apiKeyId ? undefined : companyId;

  const [isExporting, setIsExporting] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<{ number: string; isBlocked: boolean; apiKeyId: number } | null>(null);
  const [blockReason, setBlockReason] = useState("");

  // Filter sidebar state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilters);

  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  // The API key is picked from its own dropdown on the page, not the filter panel.
  const activeFilterCount = Object.entries(appliedFilters).filter(([key, v]) => key !== 'apiKeyId' && v !== '').length;

  useEffect(() => {
    if (!customerId) return;
    CustomerService.customer.getCustomer(parseInt(customerId)).catch(() => {});
  }, [customerId]);

  useEffect(() => {
    if (!customerId && !apiKeyId) return;
    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await CommunicationService.phoneNumbers.getPhoneNumbers(
          customerId ? parseInt(customerId) : null,
          {
            phoneNumbers: appliedFilters.phoneNumbers || undefined,
            fullName: appliedFilters.fullName || undefined,
            blockReason: appliedFilters.blockReason || undefined,
            status: appliedFilters.status || undefined,
            createdFrom: appliedFilters.createdFrom || undefined,
            createdTo: appliedFilters.createdTo || undefined,
            pageNumber,
            pageSize: PAGE_SIZE,
            apiKeyId: apiKeyId ? parseInt(apiKeyId) : undefined,
          }
        );
        setPhoneNumbers(response.data.phoneNumbers || []);
        setTotalCount(response.data.totalCount || 0);
      } catch {
        setError("Failed to load phone numbers");
      } finally {
        setIsLoading(false);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [customerId, apiKeyId, appliedFilters, pageNumber]);

  const refetch = async () => {
    if (!customerId && !apiKeyId) return;
    setIsLoading(true);
    try {
      const response = await CommunicationService.phoneNumbers.getPhoneNumbers(
        customerId ? parseInt(customerId) : null,
        {
          phoneNumbers: appliedFilters.phoneNumbers || undefined,
          fullName: appliedFilters.fullName || undefined,
          blockReason: appliedFilters.blockReason || undefined,
          status: appliedFilters.status || undefined,
          createdFrom: appliedFilters.createdFrom || undefined,
          createdTo: appliedFilters.createdTo || undefined,
          pageNumber,
          pageSize: PAGE_SIZE,
          apiKeyId: apiKeyId ? parseInt(apiKeyId) : undefined,
        }
      );
      setPhoneNumbers(response.data.phoneNumbers || []);
      setTotalCount(response.data.totalCount || 0);
    } catch {
      setError("Failed to load phone numbers");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTempFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    setTempFilters({ ...emptyFilters, apiKeyId: appliedFilters.apiKeyId });
    setAppliedFilters({ ...emptyFilters, apiKeyId: appliedFilters.apiKeyId });
  };

  const handleBlockPhoneNumber = async (number: string, shouldBlock: boolean, apiKeyId: number) => {
    try {
      const apiKey = await CustomerService.apiKey.getApiKeyValue(apiKeyId);
      await CommunicationService.phoneNumbers.updateBlockStatus(
        {
          phoneNumber: number,
          shouldBlock,
          reason: blockReason,
        },
        apiKey,
      );
      toast.success(`Phone number ${shouldBlock ? "blocked" : "unblocked"} successfully`);
      setBlockModalOpen(false);
      setBlockReason("");
      setSelectedPhoneNumber(null);
      refetch();
    } catch {
      toast.error(`Failed to ${shouldBlock ? "block" : "unblock"} phone number`);
    }
  };

  const handleExport = async (format: string) => {
    if (!customerId && !apiKeyId) return;
    setIsExporting(true);
    try {
      const response = await CommunicationService.phoneNumbers.exportPhoneNumbers(
        customerId ? parseInt(customerId) : null,
        {
          apiKeyId: apiKeyId ? parseInt(apiKeyId) : undefined,
          phoneNumbers: appliedFilters.phoneNumbers || undefined,
          fullName: appliedFilters.fullName || undefined,
          blockReason: appliedFilters.blockReason || undefined,
          status: appliedFilters.status || undefined,
          createdFrom: appliedFilters.createdFrom || undefined,
          createdTo: appliedFilters.createdTo || undefined,
        },
        format,
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `phone-numbers.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export phone numbers");
    } finally {
      setIsExporting(false);
    }
  };

  const openBlockModal = (number: string, isBlocked: boolean, apiKeyId: number) => {
    setSelectedPhoneNumber({ number, isBlocked, apiKeyId });
    setBlockModalOpen(true);
  };

  if (error) {
    return <div className="alert alert-danger m-4">{error}</div>;
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">Phone Numbers</h3>
          <p className="text-muted mb-0">Manage and monitor phone numbers</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ApiKeySelect
            id="phone-numbers-api-key"
            customerId={companyId}
            value={appliedFilters.apiKeyId}
            onChange={(apiKeyId) => setAppliedFilters({ ...appliedFilters, apiKeyId })}
          />
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsFilterOpen(true)}
            disabled={isLoading}
          >
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
          <ExportButton onExport={handleExport} isExporting={isExporting} size="sm" />
          <button className="btn btn-outline-primary btn-sm" onClick={refetch} disabled={isLoading}>
            <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
            Refresh
          </button>
        </div>
      </div>

      <FilterDrawer
        title="Filter Phone Numbers"
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onClear={handleClearAllFilters}
        onApply={handleApplyFilters}
      >
        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Phone Number</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search phone numbers..."
              name="phoneNumbers"
              value={tempFilters.phoneNumbers}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Full Name</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by full name..."
              name="fullName"
              value={tempFilters.fullName}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Block Reason</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by block reason..."
              name="blockReason"
              value={tempFilters.blockReason}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Status</label>
          <select
            className="form-select"
            name="status"
            value={tempFilters.status}
            onChange={handleTempFilterChange}
          >
            <option value="">All</option>
            <option value="blocked">Blocked</option>
            <option value="subscribed">Subscribed</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>
        </div>

        <hr className="my-3" />

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Created From</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="createdFrom"
              value={tempFilters.createdFrom}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Created To</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="createdTo"
              value={tempFilters.createdTo}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>
      </FilterDrawer>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : phoneNumbers.length === 0 ? (
        <div className="alert alert-info">
          {activeFilterCount > 0 ? "No phone numbers match your search criteria." : "No phone numbers found."}
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Phone Number</th>
                  <th>Status</th>
                  <th>Block Reason</th>
                  <th>Blocked At</th>
                  <th>API Key</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {phoneNumbers.map((phone) => (
                  <tr key={phone.number}>
                    <td>{phone.number}</td>
                    <td>
                      <span className={`badge bg-${phone.isBlocked ? "danger" : "success"}`}>
                        {phone.isBlocked ? "Blocked" : "Active"}
                      </span>
                    </td>
                    <td>{phone.blockReason || "-"}</td>
                    <td>{phone.blockedAt ? new Date(phone.blockedAt).toLocaleString() : "-"}</td>
                    <td className="text-nowrap">{apiKeyLabel(apiKeyNames, phone.apiKeyId)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => openBlockModal(phone.number, phone.isBlocked, phone.apiKeyId)}
                        title={phone.isBlocked ? `Blocked: ${phone.blockReason || "No reason provided"}` : "Block Phone Number"}
                      >
                        <i className={`bi bi-${phone.isBlocked ? "unlock" : "lock"}`}></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination pageNumber={pageNumber} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={setPageNumber} />
        </>
      )}

      {blockModalOpen && selectedPhoneNumber && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedPhoneNumber.isBlocked ? "Unblock Phone Number" : "Block Phone Number"}
                </h5>
                <button type="button" className="btn-close" onClick={() => { setBlockModalOpen(false); setSelectedPhoneNumber(null); setBlockReason(""); }}></button>
              </div>
              <div className="modal-body">
                {!selectedPhoneNumber.isBlocked && (
                  <div className="mb-3">
                    <label htmlFor="blockReason" className="form-label">Reason for blocking</label>
                    <textarea
                      className="form-control"
                      id="blockReason"
                      rows={3}
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Enter reason for blocking this phone number"
                      required
                    />
                  </div>
                )}
                <p>
                  Are you sure you want to {selectedPhoneNumber.isBlocked ? "unblock" : "block"} the phone number{" "}
                  <strong>{selectedPhoneNumber.number}</strong>?
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setBlockModalOpen(false); setSelectedPhoneNumber(null); setBlockReason(""); }}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-${selectedPhoneNumber.isBlocked ? "success" : "danger"}`}
                  onClick={() => handleBlockPhoneNumber(selectedPhoneNumber.number, !selectedPhoneNumber.isBlocked, selectedPhoneNumber.apiKeyId)}
                  disabled={!selectedPhoneNumber.isBlocked && !blockReason.trim()}
                >
                  {selectedPhoneNumber.isBlocked ? "Unblock" : "Block"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPhoneNumbersList;
